import React, { useState, useEffect } from 'react'
import PureTitle from '../PureTitle'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import InterestPicker from 'srcReact/components/common2/InterestPicker'
import { useDispatch, useSelector } from 'react-redux'
import { commonActions } from 'srcReact/components'
import { mixrActions } from 'srcReact/Mixr/actions'
import Spinner from 'srcReact/components/common2/Spinner'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import Dropdown1 from 'srcReact/components/common2/Dropdown1'
import { isEqual } from 'lodash'

export default function MixrInterests({
  editMode,
  showNextSection,
  hideNextButton,
  groupInterests = [],
  openEditMixrPopup,
  setDataToUpdateForRecMixr,
  previewEditMode
}) {
  const { t } = useTranslation()

  const {
    common: { selectedInterests },
    mixr: {
      editSuccess,
      editLoading,
      mixr: { id, interests: mixrInterests, expertises: mixrExpertises, interests_of, recurrence, group }
    }
  } = useSelector((state) => state)
  const [options] = useState([
    {
      value: 'group',
      label: t('event.create.interests.groupOption', {
        type:
          group?.type === 'expertise'
            ? t('formlabel.expertise', { count: 0 }).toLowerCase()
            : t('formlabel.interest', { count: 0 }).toLowerCase()
      })
    },
    {
      value: 'mixr',
      label: t('event.create.interests.mixrOption', {
        type:
          group?.type === 'expertise'
            ? t('formlabel.expertise', { count: 0 }).toLowerCase()
            : t('formlabel.interest', { count: 0 }).toLowerCase()
      })
    }
  ])
  const [interestsUpdated, setInterestsUpdated] = useState(false)
  const [initialSelectedInterests, setInitialSelectedInterests] = useState([])
  const [selectedOption, setSelectedOption] = useState(options[0])
  const [clearSelectedOptions, setClearSelectedOptions] = useState(false)
  const dispatch = useDispatch()

  const handleNext = () => {
    dispatch(mixrActions.setMixrData([{ key: 'interests', value: selectedInterests }]))
    showNextSection()
  }

  useEffect(() => {
    if (!editMode && !previewEditMode && !mixrInterests && !mixrExpertises) {
      return
    }
    if (previewEditMode && !mixrInterests && !mixrExpertises) {
      return
    }

    setInitialSelectedInterests(mixrInterests || mixrExpertises)

    const newSelectedOption =
      interests_of === 'mixr'
        ? options[1]
        : interests_of === 'group' || isEqual(mixrInterests || mixrExpertises, groupInterests)
        ? options[0]
        : options[1]

    dispatch(
      commonActions.updateSelectedInterests(
        newSelectedOption.value === 'mixr' ? mixrInterests || mixrExpertises : groupInterests
      )
    )

    if (previewEditMode) {
      setSelectedOption(interests_of ? options.find((option) => option.value === interests_of) : newSelectedOption)
    } else {
      setSelectedOption(newSelectedOption)
    }
  }, [mixrInterests, mixrExpertises, interests_of])

  useEffect(() => {
    if (editMode && editSuccess) {
      setInterestsUpdated(false)
    }
  }, [editSuccess])

  const handleSave = () => {
    if (
      selectedInterests &&
      selectedInterests.length >= appConstantSettings.MIN_INTEREST_COUNT &&
      selectedInterests.length <= appConstantSettings.MAX_INTEREST_COUNT
    ) {
      const selectedInterstsIds = selectedInterests ? selectedInterests.map((i) => i.id).join(',') : ''
      const data = {
        interest_ids: selectedInterstsIds
      }
      if (recurrence && recurrence.type !== 'none' && recurrence.is_parent_event !== 1) {
        openEditMixrPopup()
        setDataToUpdateForRecMixr(data)
      } else {
        dispatch(mixrActions.editMixr(id, data))
      }
    }
  }

  const handleCancel = () => {
    setInterestsUpdated(false)
    dispatch(commonActions.updateSelectedInterests(initialSelectedInterests))
  }

  const onInterestChange = (updatedInterests) => {
    setInterestsUpdated(true)
    setSelectedOption(options[1])
    dispatch(mixrActions.setMixrData([{ key: 'interests_of', value: options[1].value }]))
    setClearSelectedOptions(false)
    dispatch(mixrActions.setMixrData([{ key: 'interests', value: updatedInterests }]))
  }

  const handleChangeOption = (option) => {
    setInterestsUpdated(true)
    setSelectedOption(option)
    dispatch(mixrActions.setMixrData([{ key: 'interests_of', value: option.value }]))
    if (!editMode) {
      setClearSelectedOptions(true)
    }
  }

  return (
    <React.Fragment>
      <PureTitle
        title={t('event.create.interests.title', {
          type:
            group?.type === 'expertise'
              ? t('formlabel.expertise', { count: 0 }).toLowerCase()
              : t('formlabel.interest', { count: 0 }).toLowerCase()
        })}
        subTitle={t('event.create.interests.subTitle', {
          type:
            group?.type === 'expertise'
              ? t('formlabel.expertise', { count: 0 }).toLowerCase()
              : t('formlabel.interest', { count: 0 }).toLowerCase()
        })}
        isRequired={true}
      />
      <Dropdown1
        value={selectedOption}
        onChange={handleChangeOption}
        options={options}
        className={styles.dropdown}
        closeMenuOnSelect={true}
        isSearchable={false}
      />
      <InterestPicker
        componentType={group?.type}
        isMixrInterests={selectedOption?.value === 'mixr'}
        priorityInterests={groupInterests}
        origin={editMode ? 'mixr_edit' : 'mixr_creation'}
        entityId={editMode ? id : 0}
        containerCss={styles.interestPickerContainer}
        onInterestChange={onInterestChange}
        {...(selectedOption?.value === 'group' ? { existingInterests: groupInterests } : {})}
        {...(selectedOption?.value === 'mixr' && !editMode ? { clearSelectedInterests: clearSelectedOptions } : {})}
        {...(selectedOption?.value === 'mixr' && editMode
          ? { existingInterests: mixrInterests || mixrExpertises }
          : {})}
        // existingInterests={selectedOption.value === 'group' ? groupInterests : []}
        primaryButton={
          editMode
            ? interestsUpdated
              ? {
                  text:
                    editLoading && recurrence && (recurrence.type === 'none' || recurrence.is_parent_event === 1) ? (
                      <Spinner isWhite={true} />
                    ) : (
                      t('global.save')
                    ),
                  size: 'medium',
                  clickHandler: handleSave
                }
              : null
            : !hideNextButton
            ? {
                text: t('global.btnNext'),
                size: 'medium',
                clickHandler: handleNext
                //  disabled: !nextSectionVisible && (selectedInterests.length < 3 || selectedInterests.length > 15),
              }
            : null
        }
        secondaryButton={
          editMode && interestsUpdated && { text: t('global.cancel'), size: 'medium', clickHandler: handleCancel }
        }
      />
    </React.Fragment>
  )
}
